// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeModel, ProductModel } from "@insite/client-framework/Types/ApiModels";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { color, xScreen } from "@stoneage/XDL/Utilities";
import React, { createRef, ReactNode, useCallback, useEffect, useState, useRef } from "react";
import { css, ThemeProps, withTheme } from "styled-components";
import EmblaCarousel, { SlidesPerView } from "@stoneage//App/Components/EmblaCarousel";
import { CarouselStyle } from "@stoneage/App/Components/EmblaCarousel/EmblaCarousel";
import isTouchDevice from "@insite/client-framework/Common/Utilities/isTouchDevice";

interface OwnProps {
    id: string;
    extendedStyles?: CarouselStyles;
    title: string;
    hideTitle?: boolean;
    maxNumberOfColumns: number;
    slideDetails: ProductCarouselSlideDetails;
}

type Props = OwnProps & ThemeProps<BaseTheme> & HasShellContext;

export interface ProductCarouselSlideDetails {
    slides: CarouselSlide[];
    products: ProductModel[];
    attributeTypes: AttributeTypeModel[];
}

export interface CarouselSlide {
    id: string;
    renderComponent: (options: CarouselSlideOptions) => ReactNode;
}

export interface CarouselSlideOptions {}

export interface CarouselStyles {
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselContentEmptyCellGridItem?: GridItemProps;
    carouselCellGridItem?: GridItemProps;
    carouselContentContainer?: GridContainerProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    attributeLabelsCellGridItem?: GridItemProps;
    attributeLabelsGridContainer?: GridContainerProps;
    attributeLabelGridItem?: GridItemProps;
    attributeLabelText?: TypographyPresentationProps;
    attributeValuesCellGridItem?: GridItemProps;
    attributeValuesWrapper?: InjectableCss;
    attributeValuesContainer?: InjectableCss;
    attributeValuesSlidesContainer?: InjectableCss;
    attributeValueSlide?: InjectableCss;
    attributeValueSlideInner?: InjectableCss;
    attributeValuesGridContainer?: GridContainerProps;
    attributeValueGridItem?: GridItemProps;
    attributeValueText?: TypographyPresentationProps;
    // XNG-Changes
    attributeContainer?: InjectableCss;
    attributeCarousel?: CarouselStyle;
}

export const carouselStyles: CarouselStyles = {
    titleText: {
        variant: "h2",
        css: css`
            text-align: center;
            margin-bottom: 10px;
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0 auto;
        `,
    },
    prevArrowGridItem: {
        width: 1,
        css: css`
            justify-content: flex-end;
            padding-top: 230px;
            opacity: 0.5;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "common.ui6",
        css: css`
            margin-right: 45px;
            ${xScreen("lte", "sm")} {
                margin-right: 15px;
            }
            ${xScreen("lte", "xs")} {
                display: none;
            }
            border: 0;
            padding: 0;
        `,
    },
    nextArrowGridItem: {
        width: 1,
        css: css`
            justify-content: flex-start;
            padding-top: 230px;
            opacity: 0.5;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "common.ui6",
        css: css`
            ${xScreen("lte", "xs")} {
                display: none;
            }
            border: 0;
            padding: 0;
        `,
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselContentContainer: {
        gap: 0,
    },

    carouselCellGridItem: {
        width: 12,
        css: css`
            padding-bottom: 62px;
        `,
    },
    carouselWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    carouselContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            display: flex;
            justify-content: center;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
        `,
    },
    attributeLabelsCellGridItem: {
        width: 12,
        css: css`
            flex-wrap: wrap;
            padding: 0 45px;
        `,
    },

    attributeLabelGridItem: {
        width: 12,
        css: css`
            height: 60px;
            ${({ attrIndex }: any) => `padding: ${attrIndex === 0 ? `0 16px 16px 0` : `24px 16px 16px 0`}`};
        `,
    },
    attributeLabelText: {
        ellipsis: true,
        variant: "h5",
        css: css`
            margin-bottom: 0;
        `,
    },
    attributeValuesCellGridItem: {
        width: 12,
    },
    attributeValuesWrapper: {
        css: css`
            width: 100%;
            position: relative;
        `,
    },
    attributeValuesContainer: {
        css: css`
            overflow: hidden;
        `,
    },
    attributeValuesSlidesContainer: {
        css: css`
            display: flex;
        `,
    },
    attributeValueSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
        `,
    },
    attributeValueSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            padding: 0;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValuesGridContainer: {
        gap: 0,
    },
    attributeValueGridItem: {
        width: 12,
        css: css`
            &:nth-child(4n + 2) {
                background: ${color("common.ui11")};
                border: none;
            }
            padding: 12px;
            border: 1px solid ${color("common.ui4")};
            ${xScreen("gte", "md")} {
                ${({ rightBorders, leftBorders }: any) => `
                    ${rightBorders ? "" : "border-right: none;"}
                    ${leftBorders ? "" : "border-left: none;"}
                `}
            }
        `,
    },
    attributeValueText: {
        ellipsis: true,
    },
};

// XNG-Changes: change structure & styles
const Carousel = ({
    shellContext,
    theme,
    id,
    extendedStyles,
    title,
    hideTitle,
    maxNumberOfColumns,
    slideDetails,
}: Props) => {
    const afterCarousel = createRef<HTMLSpanElement>();
    const [styles] = useState(() => mergeToNew(carouselStyles, extendedStyles));

    // XNG-Change: Use refs for carousel APIs instead of embla hooks
    const mainCarouselApiRef = useRef<any>(null);
    const attributesCarouselApiRef = useRef<any[]>([]);
    const [bothApisReady, setBothApisReady] = useState(false);

    useEffect(() => {
        const currentLength = slideDetails?.attributeTypes?.length ?? 0;
        if (attributesCarouselApiRef.current.length !== currentLength) {
            attributesCarouselApiRef.current = attributesCarouselApiRef.current.slice(0, currentLength);
            setBothApisReady(false);
        }
    }, [slideDetails?.attributeTypes?.length]);

    // XNG-Change: Mark ready only when every attribute-row carousel has an API
    useEffect(() => {
        const attrApis = attributesCarouselApiRef.current;
        const expectedLength = slideDetails?.attributeTypes?.length ?? 0;
        const allAttrReady = attrApis.length === expectedLength && expectedLength > 0 && attrApis.every(Boolean);
        if (mainCarouselApiRef.current && allAttrReady && !bothApisReady) {
            setBothApisReady(true);
        }
    }, [bothApisReady, slideDetails?.attributeTypes?.length]);

    // XNG-Change: Synchronize the main carousel with every attribute-row carousel
    useEffect(() => {
        if (!bothApisReady) return;

        const mainApi = mainCarouselApiRef.current;
        const attrApis = attributesCarouselApiRef.current.filter(Boolean);

        if (!mainApi || attrApis.length === 0) return;

        let isSyncing = false;

        const syncToAttributes = () => {
            if (isSyncing) return;
            isSyncing = true;
            const snap = mainApi.selectedScrollSnap();
            attrApis.forEach(api => api.scrollTo(snap, false));
            setTimeout(() => {
                isSyncing = false;
            }, 50);
        };

        // Each attribute row gets its own handler so we know which one fired,
        // then all others (including main) are brought into alignment.
        const makesSyncToMain = (sourceApi: any) => () => {
            if (isSyncing) return;
            isSyncing = true;
            const snap = sourceApi.selectedScrollSnap();
            mainApi.scrollTo(snap, false);
            attrApis.filter(api => api !== sourceApi).forEach(api => api.scrollTo(snap, false));
            setTimeout(() => {
                isSyncing = false;
            }, 50);
        };

        const syncToMainHandlers = attrApis.map(makesSyncToMain);

        mainApi.on("select", syncToAttributes);
        attrApis.forEach((api, i) => api.on("select", syncToMainHandlers[i]));

        return () => {
            mainApi.off("select", syncToAttributes);
            attrApis.forEach((api, i) => api.off("select", syncToMainHandlers[i]));
        };
    }, [bothApisReady, slideDetails?.attributeTypes?.length]);

    const getProductAttributeValue = (product: ProductModel, attribute: AttributeTypeModel) => {
        const productAttributeValue = product.attributeTypes
            ?.filter(a => a.id === attribute.id)
            .map(a => a.attributeValues || [])
            .reduce((acc, currentValue) => [...acc, ...currentValue], []);

        if (productAttributeValue && productAttributeValue.length > 0) {
            return (
                <Typography
                    aria-describedby={`carousel-${id}_attribute-label-${attribute.id}`}
                    {...styles.attributeValueText}
                >
                    {productAttributeValue.map(o => o.valueDisplay).join(", ")}
                </Typography>
            );
        }

        return <Typography {...styles.attributeValueText}>--</Typography>;
    };

    if (!slideDetails || slideDetails.slides.length === 0) {
        return null;
    }

    const breakpoints: SlidesPerView = {
        xl: {
            slides: 4,
            gap: 22,
        },
        lg: {
            slides: 3,
            gap: 22,
        },
        sm: {
            slides: 2,
            gap: 16,
        },
        xs: {
            slides: 1,
            gap: 0,
        },
    };

    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            {hideTitle ? (
                <VisuallyHidden>
                    <Typography {...styles.titleText}>{title}</Typography>
                </VisuallyHidden>
            ) : (
                <Typography {...styles.titleText}>{title}</Typography>
            )}

            <StyledWrapper {...styles.carouselContainer} data-test-selector="productCompareCarousel">
                <EmblaCarousel
                    navigation={true}
                    navigationOutside={true}
                    scrollbar={true}
                    slidesPerView={breakpoints}
                    subGridRows={1}
                    options={{
                        align: "start",
                        loop: false,
                        watchDrag: isTouchDevice(),
                    }}
                    updateOnWindowResize={true}
                    onInit={api => {
                        mainCarouselApiRef.current = api;
                        const attrApis = attributesCarouselApiRef.current;
                        const expectedLength = slideDetails?.attributeTypes?.length ?? 0;
                        const allAttrReady =
                            attrApis.length === expectedLength && expectedLength > 0 && attrApis.every(Boolean);
                        if (allAttrReady) {
                            setBothApisReady(true);
                        }
                    }}
                    onPreviousNavigationClick={() => {
                        mainCarouselApiRef.current?.scrollPrev();
                        attributesCarouselApiRef.current.filter(Boolean).forEach(api => api.scrollPrev());
                    }}
                    onNextNavigationClick={() => {
                        mainCarouselApiRef.current?.scrollNext();
                        attributesCarouselApiRef.current.filter(Boolean).forEach(api => api.scrollNext());
                    }}
                >
                    {slideDetails.slides.map(slide => (
                        <React.Fragment key={slide.id}>{slide.renderComponent({})}</React.Fragment>
                    ))}
                </EmblaCarousel>
            </StyledWrapper>

            <StyledWrapper {...styles.carouselSlideInner}>
                {slideDetails.attributeTypes.map((attribute, index) => (
                    <StyledWrapper key={attribute.id} {...styles.attributeContainer}>
                        <Typography {...styles.attributeLabelText} className={`${index === 0 ? "first-item" : ""}`}>
                            {attribute.label.toUpperCase() || attribute.name.toUpperCase()}
                        </Typography>
                        <EmblaCarousel
                            navigation={false}
                            navigationOutside={false}
                            scrollbar={false}
                            slidesPerView={breakpoints}
                            subGridRows={1}
                            updateOnWindowResize={true}
                            options={{
                                align: "start",
                                loop: false,
                                watchDrag: isTouchDevice(),
                            }}
                            onInit={api => {
                                // XNG-Change: store each row's API at its index position
                                attributesCarouselApiRef.current[index] = api;
                                const attrApis = attributesCarouselApiRef.current;
                                const expectedLength = slideDetails?.attributeTypes?.length ?? 0;
                                const allAttrReady =
                                    attrApis.length === expectedLength && expectedLength > 0 && attrApis.every(Boolean);
                                if (mainCarouselApiRef.current && allAttrReady) {
                                    setBothApisReady(true);
                                }
                            }}
                            extendedStyles={styles.attributeCarousel}
                        >
                            {slideDetails.products.map((product, index) => (
                                <StyledWrapper
                                    key={`attribute_value_for_${product.id}`}
                                    {...styles.attributeValuesContainer}
                                >
                                    {getProductAttributeValue(product, attribute)}
                                </StyledWrapper>
                            ))}
                        </EmblaCarousel>
                    </StyledWrapper>
                ))}
            </StyledWrapper>

            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

Carousel.defaultProps = {
    maxNumberOfColumns: 4,
};

export default withIsInShell(withTheme(Carousel));
